<template>
  <div class="c-pa-weekly-sports-goal">
    <div
      class="c-pa-weekly-sports-goal__bar"
      :style="{ width: barWidth + 'px' }"
    >
      <transition-group name="list" tag="p" class="transition-group" v-if="activities.length && !disabledProgress">
        <a
          class="c-pa-weekly-sports-goal__activity"
          v-for="(activity, i) in activities"
          v-bind:key="activity + i"
          :style="{
            width: progressWidth / activities.length + 'px',
          }"
          v-on:click="!disabled && onActivityClick(activity)"
        >
          <!-- <span>{{ activity.patientActivityType.activityType.name }}</span> -->
          <activity-icon :activityType="activity.patientActivityType.key" />
        </a>
      </transition-group>
      <div
        v-else
        class="c-pa-weekly-sports-goal__activity"
      ></div>
    </div>
    <!-- Min flag  -->
    <div
      class="c-pa-weekly-sports-goal__flag"
      :style="{ left: flagLeft + 'px' }"
    ></div>
    <!-- Max flag -->
    <div
      class="c-pa-weekly-sports-goal__flag-checkered"
      :style="{ left: hundredPercentWidth + 'px' }"
    ></div>
    <!-- Progress number below moving icon = disabled -->
    <div
      class="c-pa-weekly-sports-goal__progress-number"
      :style="{
        left: progressWidth - 20 + 'px',
      }"
      v-if="progressNumber > 0 && !disabledProgress"
    >
      <span>
        <b>{{ progressNumber }}</b>
      </span>
    </div>
    <!-- Start label -->
    <div
      class="c-pa-weekly-sports-goal__flag-number"
      v-if="disabledProgress"
      :style="{ top: '30px' }"
    >
      <span>
        {{ $t('button.start', {catalog: 'app_tablet'}) }}
      </span>
    </div>
    <!-- Min flag label -->
    <div
      class="c-pa-weekly-sports-goal__flag-number"
      ref="minFlag"
      :style="{
        left: getNumberWidth(flagLeft, 'minFlag'),
        top: '50px'
      }"
    >
      <span>
        {{ $ucfirst($t('pa.minimumShort')) }} - {{ flag }}
      </span>
    </div>
    <!-- Max flag label -->
    <div
      class="c-pa-weekly-sports-goal__finish-number"
      ref="maxFlag"
      :style="{
        left: getNumberWidth(hundredPercentWidth, 'maxFlag'),
        top: '50px'
      }"
    >
      <span>
        {{ $ucfirst($t('buttons.finish', { catalog: 'app_mobile' })) }} - {{ finish }}
      </span>
    </div>
  </div>
</template>

<script>
import ActivityIcon from "./ActivityIcon.vue";

/**
 * Component PAWeeklySportsGoal.vue
 *
 * @module PAWeeklySportsGoal
 * @date
 * @author
 * @copyright UniWeb bvba 2022
 *
 * @link https://gitlab.uniweb.be/cor/4.04-caregiver-dashboard
 *
 * @coropreventionSW [SW-4.7] app/src/APP_DASHBOARD/templates/components/Parts/PAWeeklySportsGoal.vue
 * @see https://docs.google.com/document/d/1iOpHH151s_ABr-DniJIc__q-GHpXJJVq/edit#heading=h.a0z8d9xd3xr7
 *
 */

export default {
  name: "PAWeeklySportsGoal",
  components: { ActivityIcon },
  props: {
    flag: {
      type: Number,
      default: 1254,
    },
    finish: {
      type: Number,
      default: 2352,
    },
    progressNumber: {
      type: Number,
      default: 1464,
    },
    activities: {
      type: Array,
    },
    barWidth: {
      type: Number,
      default: 550,
    },
    disabled: {
      type: Boolean,
      default: false
    },
    disabledProgress: {
      type: Boolean,
      default: false
    }
  },
  computed: {
    hundredPercentWidth() {
      // maxFlag position from the end of the bar
      // in specs = 10%
      const maxFlagFromEnd = 10;
      return this.barWidth - (this.barWidth * maxFlagFromEnd / 100);
    },

    progressWidth() {
      const ratio = this.progressNumber / this.finish;
      let w = ratio * this.hundredPercentWidth;

      if (w > this.barWidth - 16) {
        return this.barWidth - 16;
      } else {
        return w;
      }
    },

    flagLeft() {
      const ratio = this.flag / this.finish;
      return ratio * this.hundredPercentWidth || 130;
    },
  },
  methods: {
    getNumberWidth(position, refName) {
      const el = this.$refs[refName]
      if (el) {
        return `${position - (el.clientWidth / 2)}px`
      }

      return `${position}px`
    },
    onActivityClick(activity) {
      this.$emit("activitySelected", activity);
    },
  },
};
</script>

<style lang="scss">
@import "../../../../scss/colors";

.c-pa-weekly-sports-goal {
  display: block;
  position: relative;
  margin-top: 16px;
  margin-bottom: 32px;
  width: 100%;
}

.c-pa-weekly-sports-goal__bar {
  display: block;
  border: 1px solid black;
  height: 32px;
  border-radius: 32px;
  width: 80vw;
  display: flex;
  align-items: flex-start;
  flex-direction: row;
  background-color: transparent;
  z-index: 10;
}

.c-pa-weekly-sports-goal__activity {
  display: flex;
  align-items: center;
  justify-content: center;
  position: relative;
  text-align: center;
  width: 24px;
  height: 30px;
  // background-color: map-get($coroColors, info-400);
  z-index: 99;
  // border-right: 1px solid black;

  &:first-of-type {
    border-top-left-radius: 30px;
    border-bottom-left-radius: 30px;
  }

  &:last-of-type {
    &:after {
      font-family: "Material Design Icons";
      content: "\F070E";
      color: map-get($coroColors, "primary-500");
      font-size: 32px;
      line-height: 32px;
      position: absolute;
      right: -16px;
      bottom: -2px;
    }
  }
}

.c-pa-weekly-sports-goal__flag {
  display: block;
  position: absolute;
  top: -1px;
  left: 130px;
  width: 2.5px;
  height: 32px;
  background-color: #FFEB3B;
  z-index: 11;

  &:before {
    font-family: "Material Design Icons";
    content: "\F023D";
    color: #FFEB3B;
    font-size: 32px;
    line-height: 32px;
    position: absolute;
    top: -28px;
    left: -6.6px;
  }
}

.c-pa-weekly-sports-goal__flag-checkered {
  display: block;
  position: absolute;
  top: 0;
  // left: 250px;
  width: 2.5px;
  height: 32px;
  background-color: #303030;
  z-index: 11;

  &:before {
    font-family: "Material Design Icons";
    content: "\F023C";
    color: #303030;
    font-size: 32px;
    line-height: 32px;
    position: absolute;
    top: -28px;
    left: -6.6px;
  }
}

.c-pa-weekly-sports-goal__progress-number {
  position: absolute;
  margin-top: 6px;
}
.c-pa-weekly-sports-goal__flag-number {
  position: absolute;
  margin-top: 6px;
}
.c-pa-weekly-sports-goal__finish-number {
  position: absolute;
  margin-top: 6px;
}

.list-enter-active,
.list-leave-active {
  transition: all 1s ease;
}
.list-enter-from,
.list-leave-to {
  opacity: 0;
  transform: transformY(30px);
}

.transition-group{
  display: inherit;
  margin-bottom: 0;
}
</style>